import { Howl } from 'howler'
import { paths, audio } from '@/scripts/config/settings'
import { startSound, safeFadeTo } from './audioUtils'
import { state, setChosenMusicIdx, getChosenMusicIdx } from './audioState'
import { getRandomInt } from '@/scripts/utils/math'
import { openedHiddenPlaces } from '@/scripts/canvas/HiddenPlace'
import { isIOS, isAndroid } from '@/scripts/helpers/features'

let tleskacSound: Howl | null = null
let tleskacTimeoutId: number | null = null

// Music functions
export function playMusic(name: string, volume: number = 1, index: number): Howl {
  const sound = startSound('music', paths.AUDIO_MUSIC_PATH, name, {
    loop: false,
    volume,
    html5: false, // Use WebAudio for better volume control
  })

  let nextIndex = index + 1
  if (nextIndex >= audio.music.length) nextIndex = 0

  sound.on('end', () => {
    state.active.music?.sound.unload()
    state.active.music = null
    const next = audio.music[nextIndex]
    playMusic(next.name, next.volume, nextIndex)
  })

  return sound
}

export function toggleMusic(): void {
  const music = state.active.music?.sound
  if (!music) return

  if (music.playing()) {
    music.pause()
  } else {
    music.play()
  }
}

export function fadeMusicVolumeUp(fadeTime: number = 500): void {
  const entry = state.active.music
  if (!entry || !entry.sound) return
  const target = entry.volume ?? 1
  safeFadeTo(entry.sound, target, fadeTime)
}

export function fadeMusicVolumeDown(targetVolume: number = 0, fadeTime: number = 350): void {
  const { ambient, music } = state.active
  if (music && music.sound) {
    safeFadeTo(music.sound, targetVolume, fadeTime)
  }
  if (ambient && ambient.sound) {
    safeFadeTo(ambient.sound, targetVolume, fadeTime)
  }
}

export function startMusic(): void {
  if (getChosenMusicIdx() == null) {
    setChosenMusicIdx(getRandomInt(0, audio.music.length - 1))
  }
  const idx = getChosenMusicIdx()!
  const m = audio.music[idx]
  const sound = playMusic(m.name, m.volume, idx)

  // Apply stored music state from localStorage
  // If user had music paused, pause it immediately after starting
  try {
    const stored = localStorage.getItem('music_active')
    if (stored === 'false') {
      // User had music paused, so pause it now
      sound.pause()
    }
  } catch {
    // Ignore localStorage errors (e.g., in private browsing mode), just continue with default behavior
  }
}

// Ambient functions
export function playAmbient(name: string): Howl {
  return startSound('ambient', paths.AUDIO_AMBIENT_PATH, name, {
    loop: true,
    html5: false, // Use WebAudio for better volume control
    volume: audio.ambient.volume,
  })
}

// Item SFX functions
export function playItemSfx(itemName: string, onComplete: (() => unknown) | null = null): Howl {
  const meta = state.sfxMeta[itemName]
  if (!meta) throw new Error(`AudioManager: SFX not in config: ${itemName}`)

  // Cycle through frames, but always start from 1 on page refresh (actual is initialized to 0)
  meta.actual = (meta.actual % meta.count) + 1
  const clipName = `${itemName}_${meta.actual}`

  let itemSound = state.itemSoundPool[clipName]
  if (!itemSound) {
    itemSound = new Howl({
      html5: false, // Use WebAudio for consistent volume control
      src: [`${paths.AUDIO_ITEMS_PATH}${clipName}.mp3`, `${paths.AUDIO_ITEMS_PATH}${clipName}.ogg`],
    })
    itemSound.on('loaderror', () => {})
    itemSound.on('playerror', () => {})
    state.itemSoundPool[clipName] = itemSound
  }

  // If this is the same sound that's currently playing, stop it immediately (no fade) so we can replay it
  const isSameSound = state.currentItemSound === itemSound
  if (isSameSound) {
    // Stop immediately without fade so we can replay right away
    if (itemSound.playing()) {
      itemSound.stop()
    }
    state.currentItemSound = null
  } else {
    // Stop any other currently playing sound with fade
    stopItemSfx()
  }

  const startPlay = () => {
    // Reset the sound to the beginning in case it was stopped mid-play
    if (itemSound.seek() > 0) {
      itemSound.seek(0)
    }
    itemSound.volume(audio.ITEMS_VOLUME)
    itemSound.play()
  }

  if (itemSound.state() === 'loaded') startPlay()
  else itemSound.once('load', startPlay)

  fadeMusicVolumeDown(meta.backgroundMusicVolume)
  itemSound.once('end', () => {
    onComplete?.()
    fadeMusicVolumeUp()
  })

  state.currentItemSound = itemSound
  return itemSound
}

export function stopItemSfx(fadeMs: number = 700): void {
  const sound = state.currentItemSound
  if (!sound) {
    fadeMusicVolumeUp()
    return
  }

  // If fadeMs is 0 or less, stop immediately
  if (fadeMs <= 0) {
    sound.stop()
    state.currentItemSound = null
    fadeMusicVolumeUp()
    return
  }

  sound.fade(sound.volume(), 0, fadeMs)
  sound.once('fade', () => {
    sound.stop()
    // keep it loaded in the pool for later reuse
  })
  state.currentItemSound = null
  fadeMusicVolumeUp()
}

// General SFX functions
export function playSfx(sfxName: string, volume: number = 1): Howl {
  const sound = new Howl({
    src: [`${paths.AUDIO_SFX_PATH}${sfxName}.mp3`, `${paths.AUDIO_SFX_PATH}${sfxName}.ogg`],
    volume,
  })
  sound.play()
  return sound
}

// -----------------------------
// Hidden-place SFX
// -----------------------------
let hiddenPlaceSound: Howl | null = null

export function playHiddenPlaceSfx(id: string): Howl {
  // Kill any previous instance immediately
  if (hiddenPlaceSound) {
    hiddenPlaceSound.stop()
    hiddenPlaceSound.unload()
    hiddenPlaceSound = null
  }

  hiddenPlaceSound = new Howl({
    src: `${paths.AUDIO_HIDDEN_PLACES_PATH}${id}.mp3`,
    volume: audio.ITEMS_VOLUME,
    html5: false, // Use WebAudio for consistent volume control
  })

  // Wait for sound to load before playing to avoid choppiness
  const playSound = () => {
    if (hiddenPlaceSound) {
      hiddenPlaceSound.play()
    }
  }

  if (hiddenPlaceSound.state() === 'loaded') {
    playSound()
  } else {
    hiddenPlaceSound.once('load', playSound)
    hiddenPlaceSound.load() // Start loading if not already
  }

  hiddenPlaceSound.once('end', () => {
    hiddenPlaceSound?.unload()
    hiddenPlaceSound = null
  })

  return hiddenPlaceSound
}

export function stopHiddenPlaceSfx(fadeMs: number = 700): void {
  if (!hiddenPlaceSound) return

  const sound = hiddenPlaceSound
  hiddenPlaceSound = null

  if (fadeMs <= 0) {
    sound.stop()
    sound.unload()
    return
  }

  // Use safeFadeTo for iOS/Android compatibility
  safeFadeTo(sound, 0, fadeMs)

  // Wait for fade to complete, then stop and unload
  // On iOS/Android, we need to manually check when fade is done
  if (isIOS || isAndroid) {
    const checkInterval = setInterval(() => {
      const currentVolume = sound.volume()
      if (currentVolume <= 0.01 || !sound.playing()) {
        clearInterval(checkInterval)
        sound.stop()
        sound.unload()
      }
    }, 50) // Check every 50ms

    // Fallback timeout
    setTimeout(() => {
      clearInterval(checkInterval)
      sound.stop()
      sound.unload()
    }, fadeMs + 100)
  } else {
    sound.once('fade', () => {
      sound.stop()
      sound.unload()
    })
  }
}
// -----------------------------
// Vehicle engine loop sounds
// -----------------------------
type VehicleId = 'auto' | 'nakladak' | 'tramvaj_prijezd' | 'tramvaj_odjezd'

function vehicleKey(id: VehicleId): string {
  return `vehicle_${id}`
}

/**
 * Start or fade in a looping vehicle sound.
 */
export function fadeInVehicleSound(id: VehicleId, fadeMs = 2000): void {
  const key = vehicleKey(id)

  startSound(key, paths.AUDIO_TRAFFIC_PATH, id, {
    loop: false, // one-shot
    html5: false, // Use WebAudio for consistent volume control
    volume: audio.TRAFFIC_VOLUME,
    fadeMs,
  })
}

/**
 * Fade out a vehicle sound, but keep it loaded & looping silently.
 * Next fadeInVehicleSound will just bring it back up.
 */
export function fadeOutVehicleSound(id: VehicleId, fadeMs = 2000): void {
  const key = vehicleKey(id)
  const entry = state.active[key]
  if (!entry) return
  safeFadeTo(entry.sound, 0, fadeMs)
}

export function playTleskacSound(): void {
  const fadeInMs = 2000
  const playMs = 5000
  const fadeOutMs = 700

  // Kill previous instance + timeout if any
  if (tleskacSound) {
    tleskacSound.stop()
    tleskacSound.unload()
    tleskacSound = null
  }
  if (tleskacTimeoutId != null) {
    clearTimeout(tleskacTimeoutId)
    tleskacTimeoutId = null
  }

  tleskacSound = new Howl({
    src: `${paths.AUDIO_EXTRAS_PATH}tleskac.mp3`,
    volume: 0,
    html5: false, // Use WebAudio for consistent volume control
    loop: true, // in case the file is short; we stop manually anyway
  })

  tleskacSound.play()

  // 2s fade in
  safeFadeTo(tleskacSound, audio.TLESKAC_VOLUME, fadeInMs)

  // After 2s fade-in + 5s at full volume, fade out 700ms
  tleskacTimeoutId = setTimeout(() => {
    if (!tleskacSound) return

    safeFadeTo(tleskacSound, 0, fadeOutMs)
    tleskacSound.once('fade', () => {
      tleskacSound?.stop()
      tleskacSound?.unload()
      tleskacSound = null
    })
    tleskacTimeoutId = null
  }, fadeInMs + playMs)
}

export function playHiddenPlaceReveal(id: string): void {
  if (openedHiddenPlaces.has(id)) return
  openedHiddenPlaces.add(id)

  new Howl({
    src: `${paths.AUDIO_EXTRAS_PATH}hidden_place_reveal.mp3`,
    volume: audio.HIDDEN_PLACE_REVEAL_VOLUME,
  }).play()
}
